import React, { useEffect, useState } from 'react';
import CurriculumMap, { SubjectType } from './CurriculumMap';

interface CurriculumMapModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const CurriculumMapModal: React.FC<CurriculumMapModalProps> = ({ isOpen, onClose }) => {
  const [currentSubject, setCurrentSubject] = useState<SubjectType>('mathematics');

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const subjectDisplayNames: Record<SubjectType, string> = {
    mathematics: 'Mathematics',
    english: 'English',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{
          background: 'rgba(10, 14, 39, 0.9)',
          backdropFilter: 'blur(8px)',
        }}
        onClick={onClose}
      />

      {/* Modal container */}
      <div
        className="relative w-full max-w-7xl max-h-[90vh] m-4 overflow-hidden flex flex-col"
        style={{
          background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 100px rgba(14, 165, 233, 0.1)',
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}
        >
          <div>
            <h2
              className="text-xl font-bold"
              style={{
                background: 'linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Curriculum Knowledge Tree
            </h2>
            <p className="text-sm" style={{ color: '#94a3b8' }}>
              Australian K-10 {subjectDisplayNames[currentSubject]} Curriculum Map
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-colors hover:bg-white/10"
            style={{ color: '#94a3b8' }}
          >
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content - CurriculumMap now includes its own legend and subject selector */}
        <div className="flex-1 overflow-auto p-6">
          <CurriculumMap
            subject={currentSubject}
            onSubjectChange={setCurrentSubject}
            showSubjectSelector={true}
          />
        </div>

        {/* Footer */}
        <div
          className="px-6 py-3 border-t flex items-center justify-between"
          style={{ borderColor: 'rgba(255, 255, 255, 0.1)' }}
        >
          <p className="text-xs" style={{ color: '#64748b' }}>
            Each node represents a curriculum module. Arrows show prerequisite relationships. Hover to highlight connections.
          </p>
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: '#64748b' }}>
              Scroll to explore
            </span>
            <svg className="w-4 h-4" style={{ color: '#64748b' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M8 9l4-4 4 4M8 15l4 4 4-4" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CurriculumMapModal;
